import { Cause, Context, Deferred, Effect, Exit, Layer, Ref, Scope } from "effect";
import { KeyboardEventsIo, type KeydownListener } from "./keyboard-ports.ts";
import { AcceptedWork } from "../state/accepted-work.ts";

export class PaletteShortcuts extends Context.Service<PaletteShortcuts, {
  readonly listen: (toggle: () => void) => Effect.Effect<Effect.Effect<void>>;
}>()("workerstation/frontend/PaletteShortcuts") {}

export const PaletteShortcutsLive = (owner: Scope.Scope) => Layer.effect(PaletteShortcuts, Effect.gen(function* () {
  const io = yield* KeyboardEventsIo, accepted = yield* AcceptedWork;
  const closed = yield* Ref.make(false);
  const pending = yield* Ref.make<ReadonlySet<Deferred.Deferred<void>>>(new Set());
  const subscriptions = yield* Ref.make<ReadonlyMap<object, Effect.Effect<void>>>(new Map());
  yield* Effect.addFinalizer(() => Effect.gen(function* () {
    yield* Ref.set(closed, true);
    yield* Effect.forEach([...(yield* Ref.get(pending))], Deferred.await);
    const removals = yield* Ref.getAndSet(subscriptions, new Map());
    const exits = yield* Effect.forEach([...removals.values()], remove => Effect.exit(remove));
    const reasons = exits.flatMap(exit => exit._tag === "Failure" ? exit.cause.reasons : []);
    if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
  }));
  return PaletteShortcuts.of({
    listen: Effect.fn("PaletteShortcuts.listen")(function* (toggle) {
      if (owner.state._tag === "Closed" || (yield* Ref.get(closed))) return yield* Effect.interrupt;
      const done = yield* Deferred.make<void>();
      yield* Ref.update(pending, state => new Set(state).add(done));
      const outcome = yield* Effect.exit(accepted.join(Effect.gen(function* () {
        const identity = {}, active = yield* Ref.make(true), removing = yield* Ref.make(false), receipt = yield* Deferred.make<Exit.Exit<void>>();
        const listener: KeydownListener = event => {
          if (owner.state._tag === "Closed" || Ref.getUnsafe(closed) || !Ref.getUnsafe(active)) return;
          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
            event.preventDefault();
            toggle();
          }
        };
        const cleanup = Effect.gen(function* () {
          if (yield* Ref.getAndSet(removing, true)) return yield* (yield* Deferred.await(receipt));
          yield* Ref.set(active, false);
          const result = yield* Effect.exit(io.remove(listener));
          yield* Deferred.succeed(receipt, result);
          yield* Ref.update(subscriptions, state => { const next = new Map(state);next.delete(identity);return next; });
          return yield* result;
        }).pipe(Effect.uninterruptible);
        yield* Ref.update(subscriptions, state => new Map(state).set(identity, cleanup));
        const added = yield* Effect.exit(io.add(listener));
        if (added._tag === "Failure") {
          const removed = yield* Effect.exit(cleanup);
          return yield* Effect.failCause(Cause.fromReasons([...added.cause.reasons, ...(removed._tag === "Failure" ? removed.cause.reasons : [])]));
        }
        return cleanup;
      })));
      yield* Deferred.succeed(done, undefined);
      yield* Ref.update(pending, state => { const next = new Set(state);next.delete(done);return next; });
      return yield* outcome;
    }, Effect.uninterruptible)
  });
}));
